import { useLayoutEffect, useState } from "react";
import { useSession, useEditorState, useLayoutVersion, useUi, nodeLabel } from "../state/context.js";
import type { EditorActions } from "../state/actions.js";
import { visibleNodeRects, type Rect } from "./geometry.js";
import { Button } from "../ui/primitives.js";
import { Icon } from "../ui/icons.js";
import { Menu } from "../ui/floating.js";
import { beginDrag } from "../dnd/drag-manager.js";

interface OverlayProps {
  container: HTMLElement;
  origin: HTMLElement;
  actions: EditorActions;
  zoom: number;
}

export function Overlays({ container, origin, actions, zoom }: OverlayProps) {
  const session = useSession();
  const selection = useEditorState((s) => s.selection);
  const hoverId = useEditorState((s) => s.hoverId);
  const editingId = useEditorState((s) => s.editingId);
  const mode = useEditorState((s) => s.mode);
  const version = useLayoutVersion();
  const drag = useUi((u) => u.drag);
  const dropTarget = useUi((u) => u.dropTarget);
  const insertion = useUi((u) => u.insertion);
  const [rects, setRects] = useState<{ selected: Array<{ id: string; rects: Rect[] }>; hover: Rect[] }>({ selected: [], hover: [] });

  useLayoutEffect(() => {
    if (mode !== "design") return setRects({ selected: [], hover: [] });
    const selected = selection.map((id) => ({ id, rects: visibleNodeRects(container, origin, id) }));
    const hover = hoverId && !selection.includes(hoverId) ? visibleNodeRects(container, origin, hoverId) : [];
    setRects({ selected, hover });
  }, [container, origin, selection, hoverId, version, zoom, mode, drag]);

  if (mode !== "design") return null;
  const primaryId = selection[0];
  const primary = rects.selected[0];
  const primaryRect = primary?.rects[0];
  const node = primaryId ? session.store.getNode(primaryId) : undefined;
  const def = node ? session.registry.get(node.type) : undefined;
  const hoverNode = hoverId ? session.store.getNode(hoverId) : undefined;
  const showToolbar = !!node && !!primaryRect && !editingId && !drag;

  return (
    <div className="bpe-overlay">
      {rects.hover.map((r, i) => (
        <div key={`h${i}`} className={`bpe-outline bpe-outline--hover ${hoverNode?.type === "section" ? "bpe-outline--section" : ""}`} style={{ left: r.x, top: r.y, width: r.width, height: r.height }}>
          {i === 0 && hoverNode ? (
            <span className="bpe-outline__label">
              <Icon name={session.registry.get(hoverNode.type)?.icon ?? "box"} size={10} /> {nodeLabel(session, hoverNode)}
            </span>
          ) : null}
        </div>
      ))}
      {rects.selected.map((s) =>
        s.rects.map((r, i) => (
          <div key={`${s.id}${i}`} className={`bpe-outline bpe-outline--selected ${session.store.getNode(s.id)?.type === "section" ? "bpe-outline--section" : ""}`} style={{ left: r.x, top: r.y, width: r.width, height: r.height }} />
        ))
      )}
      {showToolbar && node && primaryRect ? (
        <BlockToolbar node={node} rect={primaryRect} actions={actions} label={nodeLabel(session, node)} typeName={(def?.name ?? node.type).toLowerCase()} icon={def?.icon} canEdit={!!def?.canvas?.inlineEdit} />
      ) : null}
      {insertion && !drag && !editingId ? (
        <div className="bpe-caret" style={{ left: insertion.line.x, top: insertion.line.y, width: insertion.line.width }} aria-hidden="true" />
      ) : null}
      {dropTarget?.line ? <div className="bpe-drop-line" style={{ left: dropTarget.line.x, top: dropTarget.line.y, width: dropTarget.line.width }} /> : null}
      {dropTarget?.zone ? <div className={`bpe-drop-zone ${dropTarget.valid ? "" : "bpe-drop-zone--invalid"}`} style={{ left: dropTarget.zone.x, top: dropTarget.zone.y, width: dropTarget.zone.width, height: dropTarget.zone.height }} /> : null}
    </div>
  );
}

function BlockToolbar({ node, rect, actions, label, typeName, icon, canEdit }: { node: { id: string; type: string; locked?: boolean }; rect: Rect; actions: EditorActions; label: string; typeName: string; icon?: string; canEdit: boolean }) {
  const session = useSession();
  const top = rect.y - 34 < 0 ? rect.y + rect.height + 6 : rect.y - 34;
  const isSection = node.type === "section";
  const isColumn = node.type === "column";
  return (
    <div className="bpe-block-toolbar" style={{ left: Math.max(0, rect.x), top }} role="toolbar" aria-label={`${label} actions`} onPointerDown={(e) => e.stopPropagation()}>
      {!isColumn && (
        <button
          type="button"
          className="bpe-btn bpe-btn--ghost bpe-btn--icon bpe-block-toolbar__handle"
          aria-label="Drag to move"
          title="Drag to move"
          disabled={!!node.locked}
          onPointerDown={(e) => {
            e.stopPropagation();
            beginDrag(session, e, { kind: "move", payload: node.id, label, icon }, { threshold: 2 });
          }}
        >
          <Icon name="grip" />
        </button>
      )}
      <span className="bpe-block-toolbar__type">
        {label}
      </span>
      {/* Spelled out, not an icon: double-clicking to edit text is not a thing
          anyone discovers, and the pencil alone did not say what it did. */}
      {canEdit ? <Button size="sm" variant="ghost" icon="pencil" tooltip="Edit this text on the canvas" shortcut="Enter" aria-label={typeName === "text" ? "Edit this text" : `Edit this ${typeName}'s text`} className="bpe-block-toolbar__edit" onClick={() => session.store.setEditing(node.id)}>Edit text</Button> : null}
      <Button size="sm" variant="ghost" icon="arrow-up" tooltip={`Move ${typeName} up`} shortcut="Alt+↑" disabled={!!node.locked} onClick={() => actions.moveSelection(-1)} />
      <Button size="sm" variant="ghost" icon="arrow-down" tooltip={`Move ${typeName} down`} shortcut="Alt+↓" disabled={!!node.locked} onClick={() => actions.moveSelection(1)} />
      {!isColumn && <Button size="sm" variant="ghost" icon="copy" tooltip={`Duplicate ${typeName}`} shortcut="⌘D" onClick={() => actions.duplicateSelection()} />}
      <Menu
        placement="bottom-start"
        trigger={<Button size="sm" variant="ghost" icon="more" tooltip={`More ${typeName} actions`} />}
        items={[
          { id: "parent", label: "Select parent", icon: "corner-down-right", hint: "←", onSelect: () => actions.selectParent() },
          { id: "copy", label: "Copy", icon: "copy", hint: "⌘C", onSelect: () => actions.copySelection() },
          ...(isSection || isColumn ? [] : [
            { id: "wrap-card", label: "Wrap in card", icon: "panel-top", separatorBefore: true, onSelect: () => actions.wrapSelectionIn("card") },
            { id: "wrap-keep", label: "Keep together", icon: "link", onSelect: () => actions.wrapSelectionIn("keepTogether") },
            { id: "wrap-cond", label: "Make conditional", icon: "git-branch", onSelect: () => actions.wrapSelectionIn("conditional") }
          ]),
          { id: "library", label: "Save to library…", icon: "book-marked", separatorBefore: true, onSelect: () => {
            const name = window.prompt("Name this reusable block", label);
            if (name) actions.saveSelectionToLibrary(name);
          } },
          { id: "lock", label: node.locked ? "Unlock" : "Lock", icon: node.locked ? "unlock" : "lock", onSelect: () => actions.toggleLock() },
          ...(isColumn ? [] : [{ id: "delete", label: "Delete", icon: "trash", hint: "⌫", danger: true, separatorBefore: true, disabled: !!node.locked, onSelect: () => actions.deleteSelection() }])
        ]}
      />
      {!isColumn && <Button size="sm" variant="ghost" icon="trash" tooltip={`Delete ${typeName}`} shortcut="⌫" disabled={!!node.locked} onClick={() => actions.deleteSelection()} className="bpe-btn--danger" />}
    </div>
  );
}
